import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import CodeIcon from '@material-ui/icons/Code';
import ImageIcon from '@material-ui/icons/Image';
import DescriptionIcon from '@material-ui/icons/Description';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import NoteIcon from '@material-ui/icons/Note';

const useTreeItemStyles = makeStyles((theme) => ({
    root: {
        maxWidth: '430px',
        color: theme.palette.text.secondary,
        '&:hover > $content': {
            backgroundColor: '#9fa8da',
        },
        '&:focus > $content, &$selected > $content': {
            backgroundColor: `var(--tree-view-bg-color, #9fa8da)`,
            color: 'var(--tree-view-color)',
        },
        '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
            backgroundColor: 'transparent',
        },
    },
    content: {
        color: theme.palette.text.secondary,
        borderTopRightRadius: theme.spacing(2),
        borderBottomRightRadius: theme.spacing(2),
        paddingRight: theme.spacing(1),
        fontWeight: theme.typography.fontWeightMedium,
        '$expanded > &': {
            fontWeight: theme.typography.fontWeightRegular,
        },
    },
    group: {
        '& $content': {
            paddingLeft: theme.spacing(2),
        },
    },
    expanded: {},
    selected: {},
    label: {
        color: '#FFFFFF',
        fontWeight: 700,
        fontSize: "16px",
        letterSpacing: "-0.05px",
        lineHeight: "20px",
        padding: theme.spacing('5px', '0px'),
    },
}));

function StyledTreeItem(props) {
    const classes = useTreeItemStyles();
    const { filetype, labelText, color, bgColor, ...other } = props;

    return (
        <TreeItem
            nodeId={labelText}
            label={labelText}
            icon={getLabelIcon(filetype)}
            style={{ '--tree-view-color': color, '--tree-view-bg-color': bgColor }}
            classes={{ root: classes.root, content: classes.content, expanded: classes.expanded, selected: classes.selected, group: classes.group, label: classes.label }}
            {...other}
        />
    );
}

StyledTreeItem.propTypes = {
    bgColor: PropTypes.string,
    color: PropTypes.string,
    labelIcon: PropTypes.elementType.isRequired,
    labelText: PropTypes.string.isRequired,
};

const useStyles = makeStyles({
    root: {
        flexGrow: 1,
    },
});

const getLabelIcon = (filetype) => {
    if (filetype === null) {
        return null
    } else if (filetype === 'text' || filetype === 'log') {
        return <NoteIcon />
    } else if (filetype === 'html') {
        return <CodeIcon />
    } else if (filetype === 'pdf') {
        return <PictureAsPdfIcon />
    } else if (filetype === 'docx' || filetype === 'xlsx') {
        return <InsertDriveFileIcon />
    } else if (filetype === 'jpg') {
        return <ImageIcon />
    } else {
        return <DescriptionIcon />
    }
}

const StyledDirectoryViewRecursive = (props) => {
    const { data, expanded } = props

    const transformName = (name) => {
        if (name !== null && name !== undefined) {
            return name.split('_').join(' ')
        } else {
            return ''
        }
    }

    return data.map((item) => {
        if (item.type === "file") {
            return <StyledTreeItem filetype={item.filetype} nodeId={item.path} labelText={transformName(item.name)} expanded={expanded} bgColor={'#5c6bc0'} />
        } else {
            return (
                <StyledTreeItem filetype={null} nodeId={item.path} labelText={item.name} expanded={expanded}>
                    <StyledDirectoryViewRecursive data={item.childrens} expanded={expanded} />
                </StyledTreeItem>
            )
        }
    })
}

const StyledDirectoryView = (props) => {
    const classes = useStyles();
    const { data, expanded, selected, setExpanded, setSelected } = props

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const handleSelect = (event, nodeIds) => {
        setSelected(nodeIds);
    };

    return (
        <TreeView
            className={classes.root}
            defaultCollapseIcon={<FolderOpenIcon />}
            defaultExpandIcon={<FolderIcon />}
            // defaultEndIcon={<div style={{ width: 24 }} />}
            expanded={expanded}
            selected={selected}
            onNodeToggle={handleToggle}
            onNodeSelect={handleSelect}
        >
            <StyledDirectoryViewRecursive data={data} expanded={expanded} />
        </TreeView>
    );
}

StyledDirectoryView.propTypes = {
    data: PropTypes.array.isRequired,
    expanded: PropTypes.string.isRequired,
    selected: PropTypes.string.isRequired,
    setExpanded: PropTypes.func.isRequired,
    setSelected: PropTypes.func.isRequired,
    defaultExpanded: PropTypes.array.isRequired,
}

export default StyledDirectoryView
